import { t } from "@lingui/macro";
import { Button } from "@reactive-resume/ui";
import { useNavigate } from "react-router";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@reactive-resume/ui";
import { useState, useEffect } from "react";
import { authErrorAPI } from "@/client/libs/axios";
import { UserPlus, LogIn, Shield } from "lucide-react";

export interface AuthErrorData {
  error: string;
  message: string;
  redirectUrl?: string;
}

export const AuthRequiredDialog = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<AuthErrorData | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  // Listen for errors from the API
  useEffect(() => {
    // Check for existing error
    const currentError = authErrorAPI.getCurrentError();
    if (currentError) {
      setError(currentError);
      setIsOpen(true);
    }

    // Add listener for future errors
    const removeListener = authErrorAPI.addListener((newError) => {
      setError(newError);
      setIsOpen(true);
    });

    return () => {
      removeListener();
    };
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    // Clear the error after closing
    authErrorAPI.setCurrentError(null);
  };

  const handleLogin = () => {
    const redirectParam = error?.redirectUrl ? `?redirect=${encodeURIComponent(error.redirectUrl)}` : '';
    navigate(`/auth/login${redirectParam}`);
    handleClose();
  };

  const handleRegister = () => {
    const redirectParam = error?.redirectUrl ? `?redirect=${encodeURIComponent(error.redirectUrl)}` : '';
    navigate(`/auth/register${redirectParam}`);
    handleClose();
  };

  if (!error) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="space-y-3">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
            <Shield className="h-6 w-6 text-blue-600 dark:text-blue-500" />
          </div>
          <DialogTitle className="text-center text-xl">{t`Sign In Required`}</DialogTitle>
          <DialogDescription className="text-center">
            {error.message || t`You must be signed in to access this feature. Please sign up or log in to continue.`}
          </DialogDescription>
        </DialogHeader>

        <div className="bg-muted my-4 rounded-lg p-4">
          <p className="text-muted-foreground text-center text-sm">
            {t`Create an account to unlock all features and save your work.`}
          </p>
        </div>

        <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-center">
          <Button variant="outline" onClick={handleLogin} className="gap-2">
            <LogIn className="h-4 w-4" />
            {t`Sign In`}
          </Button>
          <Button onClick={handleRegister} className="gap-2">
            <UserPlus className="h-4 w-4" />
            {t`Create Account`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}; 